import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Loader,
  Modal,
  ModalButtonsContainer,
  OptionColumnCheckboxWrapper,
  OptionalColumnContainer,
  OptionalColumnName,
  OptionsModalContainer,
  SelectionHeader,
  StyledColumnRefContainer,
  StyledDragRefContainer,
  Typography
} from 'components';
import React, { FC, useState } from 'react';
import theme from 'themes';
import { ModalSize } from 'types';

interface Props {
  machineList: { id: string; name: string }[];
  setMachineList: (x: { id: string; name: string }[]) => void;
  isChangeItemOrderPopupOpen: boolean;
  onCancelReOrder: () => void;
  onSaveReOrder: () => void;
  isLoading: boolean;
}

const DraggableColumnHeader: FC<{
  machineId?: string;
  machineName?: string;
  moveRow?: (dragId: string, hoverId: string) => void;
}> = ({ machineId, machineName, moveRow }) => {
  const [isOver, setIsOver] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (machineId) {
      e.dataTransfer.setData('text/plain', machineId);
    }
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = () => {
    setIsOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const dragId = e.dataTransfer.getData('text/plain');
    if (machineId && moveRow) {
      moveRow(dragId, machineId);
    }
    setIsOver(false);
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <StyledColumnRefContainer
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      className={isDragging ? 'is-dragging' : ''}
    >
      <StyledDragRefContainer className={isOver ? 'is-over' : ''}>
        <OptionColumnCheckboxWrapper>
          <div style={{ height: '2.5rem' }}></div>
          <OptionalColumnName className={isOver ? 'is-over' : ''}>{machineName}</OptionalColumnName>
          <FontAwesomeIcon
            icon={faGripVertical}
            color={theme.colors.darkBlue}
            style={{ marginLeft: 'auto', marginTop: 'auto', marginBottom: 'auto' }}
            size="xs"
          />
        </OptionColumnCheckboxWrapper>
      </StyledDragRefContainer>
    </StyledColumnRefContainer>
  );
};

const ChangeItemOrderPopUp = ({
  machineList,
  setMachineList,
  isChangeItemOrderPopupOpen,
  onCancelReOrder,
  onSaveReOrder,
  isLoading
}: Props): JSX.Element => {
  const moveRow = (dragId: string, hoverId: string) => {
    if (!machineList || !Array.isArray(machineList)) return;

    const dragIndex = (machineList || []).findIndex((row) => row.id === dragId);
    const hoverIndex = (machineList || []).findIndex((row) => row.id === hoverId);

    if (dragIndex === hoverIndex) return;

    const newRows = [...machineList];
    const dragRow = newRows[dragIndex];

    if (!dragRow) return;

    newRows?.splice(dragIndex, 1);
    newRows?.splice(hoverIndex, 0, dragRow);

    setMachineList(newRows);
  };

  return (
    <Modal
      onClose={onCancelReOrder}
      size={ModalSize.XXSMALL_AUTO_HEIGHT}
      title="Change Machine Layout"
      visible={isChangeItemOrderPopupOpen as boolean}
      widthOverride="25rem"
    >
      {isLoading ? (
        <Loader size={50} />
      ) : (
        <OptionsModalContainer>
          <SelectionHeader>
            <Typography variant="body2" color={theme.colors.darkGrey}>
              Simply drag and drop the tiles on the order that you want to see them, you can change
              the structure and the order by simply just drag and drop the Machine name.
            </Typography>
          </SelectionHeader>
          <OptionalColumnContainer>
            {machineList?.map((el: { id: string; name: string }, i: number) => {
              return (
                <DraggableColumnHeader
                  machineId={el.id}
                  machineName={el.name}
                  key={i}
                  moveRow={moveRow}
                />
              );
            })}
          </OptionalColumnContainer>
        </OptionsModalContainer>
      )}

      <ModalButtonsContainer>
        <Button onClick={onCancelReOrder} bgColor={theme.colors.primaryBlue4}>
          Cancel
        </Button>
        <Button onClick={onSaveReOrder} variant="primary" width="5.25rem">
          Save
        </Button>
      </ModalButtonsContainer>
    </Modal>
  );
};

export default ChangeItemOrderPopUp;
